import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SortOption, FilterOption } from '@/types/crypto';
import { ArrowUpDown, ArrowDown, TrendingUp, TrendingDown, Layers, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SortFilterProps {
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  filterBy: FilterOption;
  setFilterBy: (filter: FilterOption) => void;
  counts?: {
    all: number;
    gainers: number;
    losers: number;
    defi: number;
    alerts: number;
  };
}

export const SortFilter = ({ sortBy, setSortBy, filterBy, setFilterBy, counts }: SortFilterProps) => {
  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'market_cap', label: 'MCap' },
    { value: 'volume_24h', label: 'Volume' },
    { value: 'change_24h', label: '24h' },
    { value: 'change_7d', label: '7d' },
    { value: 'tvl', label: 'TVL' },
  ];

  const filterOptions: { value: FilterOption; label: string; icon: React.ReactNode; colorClass?: string }[] = [
    { value: 'all', label: 'Todos', icon: null },
    { value: 'gainers', label: 'Alta', icon: <TrendingUp className="h-3 w-3" />, colorClass: 'text-profit' },
    { value: 'losers', label: 'Baixa', icon: <TrendingDown className="h-3 w-3" />, colorClass: 'text-loss' },
    { value: 'defi', label: 'DeFi', icon: <Layers className="h-3 w-3" />, colorClass: 'text-primary' },
    { value: 'alerts', label: 'Alertas', icon: <Bell className="h-3 w-3" />, colorClass: 'text-warning' },
  ];

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Filter Tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {filterOptions.map((option) => {
          const count = counts?.[option.value] ?? 0;
          const isActive = filterBy === option.value;
          
          return (
            <Button
              key={option.value}
              variant={isActive ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilterBy(option.value)}
              className={cn(
                "h-8 px-3 text-xs font-medium transition-all",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-sm" 
                  : "bg-card/50 hover:bg-card text-muted-foreground hover:text-foreground border border-border/50"
              )}
            >
              {option.icon && (
                <span className={cn("mr-1.5", isActive ? "" : option.colorClass)}>
                  {option.icon}
                </span>
              )}
              {option.label}
              {counts && option.value !== 'all' && count > 0 && (
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "ml-1.5 h-5 min-w-5 px-1.5 text-[10px] font-semibold",
                    isActive 
                      ? "bg-primary-foreground/20 text-primary-foreground" 
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {count}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {/* Sort Options */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <ArrowUpDown className="h-3 w-3" />
          Ordenar:
        </span>
        <div className="flex items-center gap-1">
          {sortOptions.map((option) => {
            const isActive = sortBy === option.value;
            
            return (
              <Button
                key={option.value}
                variant="ghost"
                size="sm"
                onClick={() => setSortBy(option.value)}
                className={cn(
                  "h-7 px-2.5 text-xs font-medium transition-all",
                  isActive 
                    ? "bg-accent text-accent-foreground" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {option.label}
                {isActive && <ArrowDown className="h-3 w-3 ml-1" />}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
